import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { ToastProvider } from './components/toast';
import { Spinner } from './components/ui';
import { ApiError } from './lib/api';
import { useLiveUpdates } from './lib/hooks';
import { HouseholdProvider } from './lib/household';
import { useMe } from './lib/queries';
import { AccountsPage } from './pages/Accounts';
import { AddTransactionPage } from './pages/AddTransaction';
import { HomePage } from './pages/Home';
import { InboxPage } from './pages/Inbox';
import { LoginPage } from './pages/Login';
import { PlanPage } from './pages/Plan';
import { RegisterPage } from './pages/Register';
import { SetupPage } from './pages/Setup';
import { Shell } from './shell/Shell';

// Less frequently used screens (charts, QR codes) load on demand to keep the first load small.
const ReflectPage = lazy(() => import('./pages/Reflect').then((m) => ({ default: m.ReflectPage })));
const PredictionsPage = lazy(() =>
  import('./pages/Predictions').then((m) => ({ default: m.PredictionsPage })),
);
const SettingsPage = lazy(() => import('./pages/Settings').then((m) => ({ default: m.SettingsPage })));

export function App() {
  const me = useMe();
  useLiveUpdates(!!me.data?.household);
  if (me.isPending) return <Spinner label="Opening Ovce na broju…" />;
  if (me.error) {
    if (me.error instanceof ApiError && me.error.status === 401) return <LoginPage />;
    return (
      <div className="auth">
        <div className="auth-card">
          <h1>Can't reach Ovce na broju</h1>
          <p>{me.error.message}</p>
          <button className="btn primary big" onClick={() => me.refetch()}>
            Try again
          </button>
        </div>
      </div>
    );
  }
  if (!me.data.household) return <SetupPage me={me.data} />;
  return (
    <HouseholdProvider me={me.data}>
      <ToastProvider>
        <Shell>
          <Suspense fallback={<Spinner />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/plan/:month?" element={<PlanPage />} />
              <Route path="/inbox" element={<InboxPage />} />
              <Route path="/accounts" element={<AccountsPage />} />
              <Route path="/accounts/:id" element={<RegisterPage />} />
              <Route path="/add" element={<AddTransactionPage />} />
              <Route path="/reflect" element={<ReflectPage />} />
              <Route path="/predictions" element={<PredictionsPage />} />
              <Route path="/settings/:section?" element={<SettingsPage />} />
              <Route path="/login" element={<Navigate to="/" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </Shell>
      </ToastProvider>
    </HouseholdProvider>
  );
}
